/**
 * Admin Ticket Detail Page - Redesigned
 *
 * Modern admin view for managing tickets with internal notes and assignment
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getTicket,
  addTicketMessage,
  addInternalNote,
  assignTicket,
  updateTicket,
  getAvailableAdmins,
  uploadTicketAttachment,
} from '@/services/tickets';
import type {
  Ticket,
  TicketStatus,
  TicketPriority,
  AvailableAdminsResponse,
} from '@/types';

const statusConfig = {
  open: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
  in_progress: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  waiting_user: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  resolved: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
  closed: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500' },
};

const priorityConfig = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700 border-red-300' },
};

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function AdminTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const closeLightbox = useCallback(() => setLightboxUrl(null), []);
  const [admins, setAdmins] = useState<AvailableAdminsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isReplying, setIsReplying] = useState(false);

  const [internalNote, setInternalNote] = useState('');
  const [noteFiles, setNoteFiles] = useState<File[]>([]);
  const [isAddingNote, setIsAddingNote] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState<{
    status: TicketStatus;
    priority: TicketPriority;
  } | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [activeTab, setActiveTab] = useState<'reply' | 'internal'>('reply');

  useEffect(() => {
    loadTicket();
    loadAdmins();
  }, [ticketId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const loadTicket = async () => {
    if (!ticketId) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await getTicket(Number(ticketId));
      setTicket(data);
      setEditData({
        status: data.status,
        priority: data.priority,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ticket');
      console.error('Error loading ticket:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const data = await getAvailableAdmins();
      setAdmins(data);
    } catch (err) {
      console.error('Error loading admins:', err);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !replyMessage.trim()) return;

    try {
      setIsReplying(true);
      setError(null);

      const response = await addTicketMessage(ticket.id, { message: replyMessage });
      const messageId = response.message?.id;

      if (replyFiles.length > 0 && messageId) {
        const uploadPromises = replyFiles.map((file) =>
          uploadTicketAttachment(ticket.id, file, messageId).catch((err) => {
            console.error(`Failed to upload file ${file.name}:`, err);
            return null;
          })
        );

        await Promise.all(uploadPromises);
      }

      setReplyMessage('');
      setReplyFiles([]);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reply');
      console.error('Error sending reply:', err);
    } finally {
      setIsReplying(false);
    }
  };

  const handleAddInternalNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !internalNote.trim()) return;

    try {
      setIsAddingNote(true);
      setError(null);

      const response = await addInternalNote(ticket.id, { message: internalNote });
      const messageId = response.message?.id;

      if (noteFiles.length > 0 && messageId) {
        const uploadPromises = noteFiles.map((file) =>
          uploadTicketAttachment(ticket.id, file, messageId).catch((err) => {
            console.error(`Failed to upload file ${file.name}:`, err);
            return null;
          })
        );

        await Promise.all(uploadPromises);
      }

      setInternalNote('');
      setNoteFiles([]);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add internal note');
      console.error('Error adding internal note:', err);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleAssign = async () => {
    if (!ticket || !selectedAdminId) return;

    try {
      setIsAssigning(true);
      setError(null);

      await assignTicket(ticket.id, { admin_id: selectedAdminId });
      setShowAssignModal(false);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign ticket');
      console.error('Error assigning ticket:', err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUpdate = async () => {
    if (!ticket || !editData) return;

    try {
      setIsUpdating(true);
      setError(null);

      await updateTicket(ticket.id, editData);
      setShowEditModal(false);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update ticket');
      console.error('Error updating ticket:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setReplyFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const removeReplyFile = (index: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNoteFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setNoteFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const removeNoteFile = (index: number) => {
    setNoteFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 rounded-2xl w-1/3"></div>
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 h-64 bg-gray-200 rounded-2xl"></div>
              <div className="h-64 bg-gray-200 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 p-8 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-900 mb-2">Error Loading Ticket</h3>
                <p className="text-red-800">{error}</p>
                <Link
                  to="/admin/tickets"
                  className="mt-4 inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Tickets
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const messages = ticket.messages || [];
  const status = statusConfig[ticket.status as keyof typeof statusConfig];
  const priority = priorityConfig[ticket.priority as keyof typeof priorityConfig];

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link
            to="/admin/tickets"
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors group"
          >
            <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Admin Tickets
          </Link>
        </nav>

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 p-5 shadow-sm animate-shake">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-red-800 font-medium flex-1">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Header */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-lg shadow-gray-200/50 overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm font-mono font-medium">
                        {ticket.ticket_number}
                      </span>
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full border-2 ${status.border} ${status.bg}`}>
                        <div className={`w-2 h-2 rounded-full ${status.dot} animate-pulse`}></div>
                        <span className={`text-sm font-medium ${status.text} capitalize`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium border-2 ${priority.color}`}>
                        {priority.label}
                      </div>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      {ticket.subject}
                    </h1>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl border border-gray-200">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">User</p>
                    <p className="text-sm font-medium text-gray-900">{ticket.user?.name}</p>
                    <p className="text-xs text-gray-600">{ticket.user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Account</p>
                    <p className="text-sm font-medium text-gray-900">{ticket.account?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Category</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">{ticket.category.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Created</p>
                    <p className="text-sm font-medium text-gray-900">{formatDateTime(ticket.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-lg shadow-gray-200/50 overflow-hidden">
              <div className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Conversation
                </h2>

                <div className="space-y-6 mb-8">
                  {messages.map((message) => {
                    const isInternal = message.is_internal_note;
                    const isFromAdmin = message.user?.role === 'admin';

                    return (
                      <div key={message.id} className={`flex ${isFromAdmin && !isInternal ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex gap-3 max-w-2xl ${isFromAdmin && !isInternal ? 'flex-row-reverse' : 'flex-row'}`}>
                          {/* Avatar */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                            isInternal
                              ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white'
                              : isFromAdmin
                                ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                                : 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                          }`}>
                            {message.user?.name?.charAt(0).toUpperCase() || 'U'}
                          </div>

                          {/* Message Bubble */}
                          <div className="flex-1">
                            <div className={`mb-1 flex items-center gap-2 ${isFromAdmin && !isInternal ? 'justify-end' : 'justify-start'}`}>
                              <span className="text-sm font-semibold text-gray-900">
                                {message.user?.name || 'User'}
                              </span>
                              {isInternal && (
                                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium border border-amber-300">
                                  Internal Note
                                </span>
                              )}
                              {isFromAdmin && !isInternal && (
                                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium border border-green-300">
                                  Admin
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{formatDateTime(message.created_at)}</span>
                            </div>

                            <div className={`rounded-2xl px-4 py-3 shadow-sm ${
                              isInternal
                                ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200'
                                : isFromAdmin
                                  ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                                  : 'bg-white border-2 border-gray-200'
                            }`}>
                              <p className={`text-sm whitespace-pre-wrap ${
                                isFromAdmin && !isInternal ? 'text-white' : 'text-gray-900'
                              }`}>
                                {message.message}
                              </p>

                              {/* Attachments */}
                              {message.attachments && message.attachments.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-white/20 space-y-2">
                                  {message.attachments.map((attachment) => {
                                    const isImage = attachment.mime_type?.startsWith('image/');
                                    return isImage ? (
                                      <button
                                        key={attachment.id}
                                        onClick={() => setLightboxUrl(attachment.download_url)}
                                        className="block mt-1"
                                      >
                                        <img
                                          src={attachment.download_url}
                                          alt={attachment.file_name}
                                          className="max-h-48 max-w-xs rounded-lg border border-white/20 object-cover hover:opacity-90 transition-opacity cursor-zoom-in"
                                        />
                                      </button>
                                    ) : (
                                      <a
                                        key={attachment.id}
                                        href={attachment.download_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 text-sm hover:underline ${
                                          isFromAdmin && !isInternal ? 'text-white' : 'text-indigo-600'
                                        }`}
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                        {attachment.file_name} ({formatFileSize(attachment.file_size)})
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply/Internal Note Tabs */}
                <div className="border-t-2 border-gray-100 pt-6">
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setActiveTab('reply')}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                        activeTab === 'reply'
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Reply to User
                    </button>
                    <button
                      onClick={() => setActiveTab('internal')}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                        activeTab === 'internal'
                          ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Internal Note
                    </button>
                  </div>

                  {/* Reply Form */}
                  {activeTab === 'reply' && (
                    <form onSubmit={handleReply} className="space-y-4">
                      <textarea
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-500/20 transition-all resize-none"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type your reply to the user..."
                        rows={4}
                        disabled={isReplying}
                      />

                      {/* File Upload */}
                      <div>
                        <input
                          type="file"
                          id="reply-file-upload"
                          className="hidden"
                          onChange={handleReplyFileChange}
                          multiple
                          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                          disabled={isReplying}
                        />
                        <label
                          htmlFor="reply-file-upload"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          Attach Files
                        </label>
                      </div>

                      {replyFiles.length > 0 && (
                        <div className="space-y-2">
                          {replyFiles.map((file, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 border-2 border-gray-200 rounded-lg">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeReplyFile(index)}
                                className="w-8 h-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                disabled={isReplying}
                              >
                                <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={isReplying || !replyMessage.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                          {isReplying ? (
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Sending...
                            </span>
                          ) : (
                            'Send Reply'
                          )}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Internal Note Form */}
                  {activeTab === 'internal' && (
                    <form onSubmit={handleAddInternalNote} className="space-y-4">
                      <div className="p-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
                        <p className="text-sm text-amber-800 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          This note will only be visible to admins, not to the user
                        </p>
                      </div>

                      <textarea
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20 transition-all resize-none"
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        placeholder="Type an internal note..."
                        rows={4}
                        disabled={isAddingNote}
                      />

                      <div>
                        <input
                          type="file"
                          id="note-file-upload"
                          className="hidden"
                          onChange={handleNoteFileChange}
                          multiple
                          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                          disabled={isAddingNote}
                        />
                        <label
                          htmlFor="note-file-upload"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          Attach Files
                        </label>
                      </div>

                      {noteFiles.length > 0 && (
                        <div className="space-y-2">
                          {noteFiles.map((file, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 border-2 border-gray-200 rounded-lg">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeNoteFile(index)}
                                className="w-8 h-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                disabled={isAddingNote}
                              >
                                <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={isAddingNote || !internalNote.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                          {isAddingNote ? (
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Adding...
                            </span>
                          ) : (
                            'Add Internal Note'
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-lg shadow-gray-200/50 p-6 sticky top-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Admin Actions
              </h3>

              <button
                onClick={() => setShowEditModal(true)}
                className="w-full px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Status & Priority
              </button>

              <button
                onClick={() => setShowAssignModal(true)}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {ticket.assignedAdmin ? 'Reassign Ticket' : 'Assign Ticket'}
              </button>

              {ticket.assignedAdmin && (
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-100">
                  <p className="text-xs font-medium text-blue-600 mb-2">Currently Assigned To</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                      {ticket.assignedAdmin.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{ticket.assignedAdmin.name}</p>
                      <p className="text-xs text-gray-600">{ticket.assignedAdmin.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assign Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-blue-500 to-cyan-600">
                <h3 className="text-xl font-bold text-white">Assign Ticket</h3>
              </div>
              <div className="p-6 space-y-4">
                <select
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all"
                  value={selectedAdminId || ''}
                  onChange={(e) => setSelectedAdminId(Number(e.target.value))}
                >
                  <option value="">Select admin...</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name} ({admin.email})
                    </option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAssignModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                    disabled={isAssigning}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100"
                    disabled={isAssigning || !selectedAdminId}
                  >
                    {isAssigning ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600">
                <h3 className="text-xl font-bold text-white">Edit Ticket</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Status</label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all"
                    value={editData.status}
                    onChange={(e) =>
                      setEditData({ ...editData, status: e.target.value as TicketStatus })
                    }
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting_user">Waiting for User</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Priority</label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all"
                    value={editData.priority}
                    onChange={(e) =>
                      setEditData({ ...editData, priority: e.target.value as TicketPriority })
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100"
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Image lightbox */}
    {lightboxUrl && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={closeLightbox}
      >
        <div className="relative max-w-5xl max-h-screen p-4" onClick={e => e.stopPropagation()}>
          <button
            onClick={closeLightbox}
            className="absolute -top-2 -right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-800 shadow-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
          />
          <a
            href={lightboxUrl}
            download
            onClick={e => e.stopPropagation()}
            className="mt-3 flex items-center justify-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>
        </div>
      </div>
    )}
    </>
  );
}
